/**
 * The element stack inside a table cell — reading it, and the operations that
 * rewrite it.
 *
 * Split out of `table-ops`, which had grown to 833 lines holding two unrelated
 * models: the table's STRUCTURE (rows, columns, merges, cell styling) and the
 * stack of elements a single cell happens to contain. Nothing here touches a
 * row or a column beyond addressing one, and nothing over there reads an
 * element — the two halves shared a file and a `table*` prefix and little else,
 * so the import points one way only.
 *
 * The distinction that makes the file worth splitting is also the one that
 * produced the bugs: `selectedElementIndex` is a POSITION into the array below,
 * so the operations that resize that array own the job of keeping it valid. That
 * rule now lives beside the only code that can break it.
 */
import type {CellElement, TableCellData, TableConfig, TableRowData} from './table-types';
import {labelText} from './label-text';
import {storedMarks, type RichText} from './rich-text';

export function createDefaultCellElement(type: CellElement['type']): CellElement {
  switch (type) {
    case 'heading':
      return {type, label: 'Heading', config: {}};
    case 'text':
      return {
        type,
        label: 'Label',
        config: {input_type: 'textarea', placeholder: 'Enter text…'},
      };
    case 'photo':
      // Square, and still stretched to its column: the ratio renders against
      // whatever width the column gives it, so no explicit width is needed --
      // which is the only way to have both. `height` stays as the size to fall
      // back to if the author unlocks the ratio.
      return {type, label: 'Image input', config: {height: 80, square: true}};
    case 'logo':
      return {
        type,
        label: '',
        config: {width: 80, height: 40, align: 'left', object_fit: 'contain'},
      };
    case 'checkbox':
      return {
        type,
        label: 'Options',
        config: {options: ['Option 1', 'Option 2'], multi_select: true},
      };
    case 'dropdown':
      return {type, label: 'Select', config: {options: ['Option 1', 'Option 2']}};
    case 'signature':
      return {type, label: 'Signature', config: {}};
  }
}

// Normalizes a cell's elements — handles both the legacy single `element` field and the new `elements[]`.
export function getCellElements(cell: TableCellData): CellElement[] {
  if (cell.elements && cell.elements.length > 0) return cell.elements;
  if (cell.element) return [cell.element];
  return [];
}

/**
 * Whether a cell element offers a drag-resize corner.
 *
 * Only the two that draw a box an author sizes. And **not a photo locked to
 * 1:1**: the drag has only ever written `height`, while a square box renders
 * from a ratio and no height at all (see `photoBoxStyle`) — so the box followed
 * the pointer and then snapped back on release, which since `square` became the
 * default for a new image input was the default path rather than an edge case.
 *
 * Refused rather than taught the lock, because for an image input stretched to
 * its column there is no height to drag TO: the column sets the width and the
 * ratio sets the rest. The Ratio button that turns the lock off sits in the
 * toolbar beside W/H, so unlocking is one click away from wanting it.
 */
export function canDragResize(el: CellElement): boolean {
  if (el.type === 'logo') return true;
  return el.type === 'photo' && el.config.square !== true;
}

/**
 * The width this element carries, for the types that have one.
 *
 * The one fact the two questions below both rest on: whether there is free space
 * around the box, which is what an alignment has to move it within, and what the
 * W field edits. Stated once so the two cannot come to disagree about which
 * types have a width at all.
 *
 * A `heading` has none — it aligns as TEXT, through the cell's own text style,
 * which already works. A `logo` has one and is still excluded, because it aligns
 * itself through `config.align`: its own field with its own control, shared with
 * standalone logos where there is no cell to inherit from.
 */
export function cellElementWidth(el: CellElement): number | undefined {
  switch (el.type) {
    case 'photo':
    case 'signature':
    case 'text':
    case 'checkbox':
    case 'dropdown':
      return el.config.width;
    case 'heading':
    case 'logo':
      return undefined;
  }
}

/**
 * The types whose width is set by TYPING it rather than by dragging.
 *
 * An image input and a signature are drag-resized on the canvas and have their
 * own W/H fields in their panels; these three had no width at all until it
 * became the knob that makes them alignable, so the field lives beside the
 * alignment it enables. Blank spans the cell, which is what every one of them
 * did before and still does.
 *
 * Here rather than in the toolbar that draws the field: it is a fact about the
 * element, it overlaps `canAlignInCell` exactly where the W field is what
 * unlocks the align buttons, and a copy of that list sitting in a component was
 * a copy that could disagree with this one.
 */
export function canSizeInCell(
  el: CellElement,
): el is Extract<CellElement, {type: 'text' | 'checkbox' | 'dropdown'}> {
  return el.type === 'text' || el.type === 'checkbox' || el.type === 'dropdown';
}

/**
 * Whether this element can be aligned across its cell — meaning MOVED, at the
 * size it already is.
 *
 * **Aligning never resizes.** That is the whole rule, and it is why this narrows
 * to an element carrying a width of its own. Alignment is expressed as
 * `justify-content` (see `cellItemStyle`), which distributes the free space in a
 * row and leaves a sized child alone — but a child with no width of its own
 * becomes shrink-to-fit the moment its wrapper turns into a flex row, so
 * aligning it would resize it. Unsized, every one of these spans its cell
 * deliberately: the author sizes it by dragging the column edge, and a box with
 * no free space around it can only be moved by shrinking it. So it is not
 * offered, and — via this same predicate — a centred CELL does not flex it
 * either. Give it a width and it becomes alignable: one knob, both questions.
 *
 * The one exception is a checkbox: it draws no frame across the cell, only its
 * boxes and their text, so shrinking it to fit its options resizes nothing the
 * author can see. It is alignable unsized and moves as a block, boxes lined up.
 */
export function canAlignInCell(el: CellElement): boolean {
  if (el.type === 'checkbox') return true;
  return cellElementWidth(el) !== undefined;
}

/**
 * A cell's element stack, and which of them is selected.
 *
 * One reader because `selectedElementIndex` is a stored POSITION with an
 * implicit default, and eleven places were re-deriving it as
 * `cfg.selectedElementIndex ?? 0` — the canvas ring twice, the label commit
 * three times, the keyboard handlers, the template shortcuts, the toolbar's
 * Label field and the context menu. Only one of them clamped, so an index left
 * past the end of a shortened stack read as an element that is not there;
 * `tableCellUpdateElementAt` against it writes nothing, and the toolbar's Label
 * field bound to it edits whatever landed in that slot. The `?? 0` is the same
 * default the canvas draws its ring with when nothing has been clicked yet.
 *
 * `index` is 0 for an empty cell, where `element` is `undefined` — there is no
 * meaningful position, and every caller has to handle an empty stack anyway.
 */
export function cellSelection(
  cfg: Pick<TableConfig, 'rows' | 'selectedElementIndex'>,
  ri: number,
  ci: number,
): {stack: CellElement[]; index: number; element: CellElement | undefined} {
  const stack = getCellElements(cfg.rows[ri]?.cells[ci] ?? {id: '', content: ''});
  const index = Math.min(Math.max(cfg.selectedElementIndex ?? 0, 0), Math.max(stack.length - 1, 0));
  return {stack, index, element: stack[index]};
}

// Appends an element to a cell's element stack.
export function tableCellInsertElement(
  cfg: TableConfig,
  ri: number,
  ci: number,
  element: CellElement,
): TableConfig {
  return {
    ...cfg,
    rows: cfg.rows.map((row, rIdx) =>
      rIdx !== ri
        ? row
        : {
            ...row,
            cells: row.cells.map((cell, cIdx) => {
              if (cIdx !== ci) return cell;
              const existing = getCellElements(cell);
              return {...cell, elements: [...existing, element], element: undefined};
            }),
          },
    ),
  };
}

// Removes all elements from a cell.
export function tableCellRemoveElement(cfg: TableConfig, ri: number, ci: number): TableConfig {
  return {
    ...cfg,
    rows: cfg.rows.map((row, rIdx) =>
      rIdx !== ri
        ? row
        : {
            ...row,
            cells: row.cells.map((cell, cIdx) =>
              cIdx !== ci ? cell : {...cell, elements: [], element: undefined},
            ),
          },
    ),
  };
}

// Replaces the element at a specific index in a cell.
export function tableCellUpdateElementAt(
  cfg: TableConfig,
  ri: number,
  ci: number,
  idx: number,
  element: CellElement,
): TableConfig {
  return {
    ...cfg,
    rows: cfg.rows.map((row, rIdx) =>
      rIdx !== ri
        ? row
        : {
            ...row,
            cells: row.cells.map((cell, cIdx) => {
              if (cIdx !== ci) return cell;
              const elements = getCellElements(cell).map((el, i) => (i === idx ? element : el));
              return {...cell, elements, element: undefined};
            }),
          },
    ),
  };
}

/**
 * Remove the element at a specific index in a cell, **carrying the selection**.
 *
 * The same reason `tableCellMoveElement` does: `selectedElementIndex` is a
 * position, so shortening the stack under it leaves it describing an element
 * that is not there — or, when the last one goes, an empty cell. Every caller
 * used to fix this up itself with the same `Math.min(idx, remaining - 1)`, once
 * in the Delete key handler and once in the template shortcuts, and a third
 * copy went to the canvas `x` button before that button was removed. The op
 * that invalidates the index is the one place that can be relied on to mend it.
 */
export function tableCellRemoveElementAt(
  cfg: TableConfig,
  ri: number,
  ci: number,
  idx: number,
): TableConfig {
  const remaining = Math.max(
    getCellElements(cfg.rows[ri]?.cells[ci] ?? {id: '', content: ''}).length - 1,
    0,
  );
  return {
    ...cfg,
    // Clamped rather than remapped: removing shortens the stack, so the only
    // question is whether the selection still has a position to point at.
    selectedElementIndex:
      remaining === 0 ? null : Math.min(cfg.selectedElementIndex ?? 0, remaining - 1),
    rows: cfg.rows.map((row, rIdx) =>
      rIdx !== ri
        ? row
        : {
            ...row,
            cells: row.cells.map((cell, cIdx) => {
              if (cIdx !== ci) return cell;
              const elements = getCellElements(cell).filter((_, i) => i !== idx);
              return {...cell, elements, element: undefined};
            }),
          },
    ),
  };
}

/**
 * Move an element within a cell's stack, **carrying the selection with it**.
 *
 * `selectedElementIndex` is a position, not an identity, so a reorder moves the
 * ground under it: moving the selected element left it pointing at whichever
 * element slid into the vacated slot. Everything that reads it then described a
 * different element than the author had picked -- the canvas ring, the next Move
 * up / Move down, Delete, and the toolbar, whose Label field is bound to the
 * selected element and so filled itself with the neighbour's label. Moving a
 * text label above an image input made that field read "Image input", and typing
 * in it renamed the image input.
 *
 * Remapped through the same permutation rather than special-cased: an index
 * between `fromIdx` and `toIdx` shifts by one even when it is not the element
 * being moved, and "did the selected one move?" is only the loudest case.
 */
export function tableCellMoveElement(
  cfg: TableConfig,
  ri: number,
  ci: number,
  fromIdx: number,
  toIdx: number,
): TableConfig {
  if (fromIdx === toIdx) return cfg;
  const stackLength = getCellElements(cfg.rows[ri]?.cells[ci] ?? {id: '', content: ''}).length;
  return {
    ...cfg,
    selectedElementIndex: remapIndex(cfg.selectedElementIndex, stackLength, fromIdx, toIdx),
    rows: cfg.rows.map((row, rIdx) =>
      rIdx !== ri
        ? row
        : {
            ...row,
            cells: row.cells.map((cell, cIdx) => {
              if (cIdx !== ci) return cell;
              const elements = [...getCellElements(cell)];
              const [moved] = elements.splice(fromIdx, 1);
              if (moved !== undefined) elements.splice(toIdx, 0, moved);
              return {...cell, elements, element: undefined};
            }),
          },
    ),
  };
}

/** Where index `selected` ends up when `fromIdx` is spliced out and re-inserted
 *  at `toIdx`. `null` (nothing selected) and an out-of-range index pass through
 *  -- there is no position to follow. */
function remapIndex(
  selected: number | null | undefined,
  length: number,
  fromIdx: number,
  toIdx: number,
): number | null {
  if (selected == null || selected < 0 || selected >= length) return selected ?? null;
  const order = Array.from({length}, (_, i) => i);
  const [moved] = order.splice(fromIdx, 1);
  if (moved !== undefined) order.splice(toIdx, 0, moved);
  const next = order.indexOf(selected);
  return next === -1 ? selected : next;
}

/**
 * Every cell element in a table, in document order, paired with the row and cell
 * that hold it and its index in that cell's stack.
 *
 * A generator, so a caller can stop early (`templateHasLogoId` bails on the
 * first hit) and so the walkers that used to hand-roll rows → cells → elements
 * share one traversal. Callers that REBUILD a table still map the rows
 * themselves: reading and rewriting are different shapes, and forcing both
 * through one helper would buy indirection rather than clarity.
 */
/**
 * Every cell element in a table, rebuilt through `fn` — the write half of
 * `cellElements`.
 *
 * **Identity is the contract.** A row, a cell or the whole config that `fn`
 * left alone comes back as the SAME reference, so every memo downstream of the
 * requirements survives a pass that changed nothing — and the overwhelmingly
 * common case (a walk that finds nothing to do) costs one traversal and no
 * allocation. A caller can therefore test `next === cfg` instead of tracking
 * "did anything change" itself.
 *
 * Written once because it was written twice: `rehydrateLogoUrls` and
 * `uploadPendingLogos` each carried this rows → cells → elements walk, the same
 * three `every((x, i) => x === before[i])` guards, and the same normalisation
 * comment below, differing only in what they did to the element at the bottom.
 * That is the shape of a helper, not of two features.
 *
 * `fn` is synchronous on purpose. A walk that awaits per element interleaves the
 * work with the rebuild and leaves the tree half-applied if one call rejects;
 * a caller with async work resolves it FIRST and hands the answers in — see
 * `uploadTableLogos`.
 */
export function mapTableCellElements(
  cfg: TableConfig,
  fn: (
    el: CellElement,
    addr: {row: TableRowData; cell: TableCellData; index: number},
  ) => CellElement,
): TableConfig {
  const before = cfg.rows ?? [];
  const rows = before.map((row) => {
    const cells = row.cells.map((cell) => {
      const elements = getCellElements(cell);
      const next = elements.map((el, index) => fn(el, {row, cell, index}));
      if (next.every((el, i) => el === elements[i])) return cell;
      // Normalize onto `elements[]` and drop the legacy single `element` field,
      // the same way the table's own edit operations do.
      return {...cell, elements: next, element: undefined};
    });
    return cells.every((cell, i) => cell === row.cells[i]) ? row : {...row, cells};
  });
  if (rows.every((row, i) => row === before[i])) return cfg;
  return {...cfg, rows};
}

export function* cellElements(
  cfg: TableConfig,
): Generator<{el: CellElement; row: TableRowData; cell: TableCellData; index: number}> {
  for (const row of cfg.rows ?? []) {
    for (const cell of row.cells) {
      for (const [index, el] of getCellElements(cell).entries()) {
        yield {el, row, cell, index};
      }
    }
  }
}

/**
 * What a cell's editor opens with: the selected element's label, or the cell's
 * own text — each with the styles on part of it (DERP-307).
 */
export function cellEditText(
  cell: TableCellData | undefined,
  element: CellElement | null | undefined,
): RichText {
  if (element) return labelText(element);
  return cell ? cellText(cell) : {text: ''};
}

/** A cell's own text, with the styles on part of it. */
export function cellText(cell: TableCellData): RichText {
  return {text: cell.content, marks: cell.content_marks};
}

/** `cell` with an edit of its own text written back. */
export function withCellText(cell: TableCellData, draft: RichText): TableCellData {
  return {...cell, content: draft.text, content_marks: storedMarks(draft.text, draft.marks)};
}
